import {
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  UseQueryOptions,
} from 'react-query';
import { AxiosError } from 'axios';
import { CheckoutPlan } from '~/types/models';
import axios from 'axios';
import { DefaultGetResponse } from '~/App/Api/rest';
import { AddOnCheckoutDetails, AddOnType } from '~/types/models/addon';
const { team } = window.Bonjoro;

export const queryKeys = {
  all: ['checkout'] as const,
  checkoutPlans: () => [...queryKeys.all, 'checkout-plans'] as const,
  checkoutPlan: (plan: string) => [...queryKeys.checkoutPlans(), plan] as const,
  checkoutAddOns: () => [...queryKeys.all, 'checkout-add-ons'] as const,
  checkoutAddOn: (addOn: AddOnType) =>
    [...queryKeys.checkoutAddOns(), addOn] as const,
};

export interface GetCheckoutPlanArgs {
  plan: string;
}

export async function getCheckoutPlan({ plan }: GetCheckoutPlanArgs) {
  const res = await axios.get<CheckoutPlan>(`/api/v3/checkout/plan/${plan}`);
  return res.data;
}

export interface UseCheckoutPlanParams extends GetCheckoutPlanArgs {
  options?: UseQueryOptions<CheckoutPlan, AxiosError>;
}

export const useCheckoutPlan = ({ plan, options }: UseCheckoutPlanParams) =>
  useQuery<DefaultGetResponse<CheckoutPlan>, AxiosError>(
    queryKeys.checkoutPlan(plan),
    () => getCheckoutPlan({ plan }),
    options
  );

export interface UseCheckoutPlansParams {
  plans: string[];
  options?: UseQueryOptions<CheckoutPlan, AxiosError>;
}

/**
 * This is for special cases that a legacy user wants to checkout both TM and VM plan.
 * Creating an API is an overkill because most of the time, user will checkout one plan at a time.
 */
export const useCheckoutPlans = ({ plans, options }: UseCheckoutPlansParams) =>
  useQueries(
    plans.map<UseQueryOptions<CheckoutPlan, AxiosError>>((plan) => {
      return {
        queryKey: queryKeys.checkoutPlan(plan),
        queryFn: () => getCheckoutPlan({ plan }),
        ...options,
      };
    })
  );

export interface GetCheckoutAddOnArgs {
  addOn: AddOnType;
}

export async function getCheckoutAddOn({ addOn }: GetCheckoutAddOnArgs) {
  const res = await axios.get<AddOnCheckoutDetails>(
    `/api/v3/teams/${team.id}/checkout/addon/${addOn}`
  );
  return res.data;
}

export interface UseCheckoutAddOnParams extends GetCheckoutAddOnArgs {
  options?: UseQueryOptions<AddOnCheckoutDetails, AxiosError>;
}

export const useCheckoutAddOn = ({ addOn, options }: UseCheckoutAddOnParams) =>
  useQuery<DefaultGetResponse<AddOnCheckoutDetails>, AxiosError>(
    queryKeys.checkoutAddOn(addOn),
    () => getCheckoutAddOn({ addOn }),
    options
  );

export interface ProceedCheckoutPostResponse {
  status: string;
}

export interface ProceedCheckoutPlanArgs {
  planSlug: string;
  intentId?: string;
  legacyTeam?: boolean;
}

export async function proceedCheckoutPlan({
  planSlug,
  intentId,
  legacyTeam,
}: ProceedCheckoutPlanArgs) {
  const params = {};

  if (intentId) {
    params['intent_id'] = intentId;
  }

  if (legacyTeam) {
    params['legacy_team'] = legacyTeam;
  }

  const result = await axios.post<ProceedCheckoutPostResponse>(
    `/api/v3/checkout/${planSlug}`,
    params
  );
  return result.data;
}

export interface ProceedCheckoutPlanParams {
  options?: UseMutationOptions<
    ProceedCheckoutPostResponse,
    AxiosError,
    ProceedCheckoutPlanArgs
  >;
}

export const useProceedCheckoutPlan = ({
  options,
}: ProceedCheckoutPlanParams) =>
  useMutation<ProceedCheckoutPostResponse, AxiosError, ProceedCheckoutPlanArgs>(
    (args: ProceedCheckoutPlanArgs) => proceedCheckoutPlan(args),
    options
  );

export interface ProceedCheckoutAddOnArgs {
  addOn: AddOnType;
  paymentMethodId?: string;
}

export async function proceedCheckoutAddOn({
  addOn,
  paymentMethodId,
}: ProceedCheckoutAddOnArgs) {
  const params = {
    addon: addOn,
  };

  if (paymentMethodId) {
    params['payment_method_id'] = paymentMethodId;
  }

  const result = await axios.post<ProceedCheckoutPostResponse>(
    `/api/v3/teams/${team.id}/addons`,
    params
  );
  return result.data;
}

export interface ProceedCheckoutAddOnParams {
  options?: UseMutationOptions<
    ProceedCheckoutPostResponse,
    AxiosError,
    ProceedCheckoutAddOnArgs
  >;
}

export const useProceedCheckoutAddOn = ({
  options,
}: ProceedCheckoutAddOnParams) =>
  useMutation<
    ProceedCheckoutPostResponse,
    AxiosError,
    ProceedCheckoutAddOnArgs
  >((args: ProceedCheckoutAddOnArgs) => proceedCheckoutAddOn(args), options);
